import type { ReactNode } from "react";
import { useId } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import type { TextAreaProps as CoreTextAreaProps } from "@jti/core-components";
import { TextArea as CoreTextArea } from "@jti/core-components";

import styles from "./TextArea.module.scss";

export type TextAreaProps = {
  label?: string;
  labelId?: string;
  successText?: ReactNode;
  errorText?: ReactNode;
  helperText?: ReactNode;
  isError?: boolean;
  isSuccess?: boolean;
  isOptionalLabelVisible?: boolean;
  necessityIndicator?: boolean;
  optionalText?: string;
  isCharacterCountVisible?: boolean;
  containerClassName?: string;
  value?: string | number | undefined;
} & Omit<CoreTextAreaProps, "value">;

const getStatus = (isError?: boolean, isSuccess?: boolean): CoreTextAreaProps["status"] => {
  if (isError) return "error";
  if (isSuccess) return "success";
  return "default";
};

// DS3 renders the asterisk for every required field, so the necessity-indicator variant is used
// for optional fields. Consumers that don't want an asterisk at all (e.g. Contact Us form, per
// design) can opt into the necessity-indicator variant themselves via necessityIndicator.
const getLabelFields = (
  label: string | undefined,
  necessityIndicator: boolean | undefined,
  required: boolean | undefined,
  optionalText: string | undefined
): CoreTextAreaProps["labelFields"] => {
  if (!label) return undefined;
  if (necessityIndicator) return { label, necessityIndicator: true, optionalText };
  if (required) return { label, asterisk: true };

  return { label, necessityIndicator: true };
};

const getDisplayedHelperText = ({
  status,
  successText,
  errorText,
  helperText,
}: {
  status: TextAreaProps["status"];
  successText: TextAreaProps["successText"];
  errorText: TextAreaProps["errorText"];
  helperText: TextAreaProps["helperText"];
}) => {
  let supportingText = helperText;
  if (status === "success" && successText) {
    supportingText = successText;
  } else if (status === "error" && errorText) {
    supportingText = errorText;
  }

  return supportingText;
};

export const TextArea = ({
  isOptionalLabelVisible,
  necessityIndicator,
  optionalText,
  isCharacterCountVisible,
  id,
  label,
  helperText,
  isError,
  errorText,
  isSuccess,
  successText,
  containerClassName,
  labelId,
  value,
  required,
  ref,
  ...rest
}: TextAreaProps) => {
  const { name } = rest;
  const { __ } = useTranslations();
  const fallbackId = useId();
  const textAreaId = id || name || fallbackId;

  // TODO: isOptionalLabelVisible (label-concatenation) and necessityIndicator (FormFieldLabel's
  // indicator span) are two separate mechanisms for showing "(optional)"/"(required)" next to the
  // label. Consolidate on necessityIndicator and drop isOptionalLabelVisible in a follow-up.
  const optionalLabelText = label && !required && isOptionalLabelVisible ? __("form_fields.optional") : "";
  const displayedLabel = optionalLabelText ? `${label} ${optionalLabelText}` : label;

  const labelFields = getLabelFields(displayedLabel, necessityIndicator, required, optionalText);

  const status = getStatus(isError, isSuccess);
  const displayedHelperText = getDisplayedHelperText({
    status,
    successText,
    errorText,
    helperText,
  });

  return (
    <div className={classNames(styles.container, containerClassName)}>
      <CoreTextArea
        {...rest}
        ref={ref}
        required={required}
        id={textAreaId}
        value={value}
        labelFields={labelFields}
        labelId={labelId}
        status={status}
        helperText={displayedHelperText}
        showCharacterCount={isCharacterCountVisible}
      />
    </div>
  );
};
